import { useStore } from '@nanostores/react';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetPaymentsOverview } from '~/lib/api/payments';
import { AccountsSection } from './AccountsSection';
import { GoLiveCard } from './GoLiveCard';
import { OffersSection } from './OffersSection';
import { PaymentsList } from './PaymentsList';
import { liveStateMeta } from './payments-model';
import { SettingsSection } from './SettingsSection';
import { LoadError, Pill, Section, SkeletonCard } from './ui';
import { useResource } from './use-resource';

/**
 * Owner-facing Payments plugin panel. The app id IS the chat id, so this works
 * before the site is published (preview checkouts are always test mode). Each
 * section loads on its own: the overview (accounts, settings, go-live), the
 * payments list and the buy buttons never block one another.
 */
export function PaymentsPanel() {
  const appId = useStore(chatId);

  return (
    <div className="flex h-full flex-col">
      {appId ? (
        <PaymentsPanelBody key={appId} appId={appId} />
      ) : (
        <>
          <PanelHeader />
          <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
            Start building to set up payments.
          </div>
        </>
      )}
    </div>
  );
}

function PanelHeader({ pill, note }: { pill?: React.ReactNode; note?: string | null }) {
  return (
    <div className="border-b border-hairline px-4 py-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
            <div className="i-ph:credit-card text-[13px] text-accent" />
          </div>
          <h3 className="text-[13px] font-medium tracking-tight text-primary">Payments</h3>
        </div>
        {pill}
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted">
        Preview is always test mode. Live payments start when you go live.
      </p>
      {note && <p className="mt-1 text-[11px] leading-relaxed text-warning">{note}</p>}
    </div>
  );
}

function PaymentsPanelBody({ appId }: { appId: string }) {
  const overview = useResource(() => apiGetPaymentsOverview(appId), appId);
  const settings = overview.data?.settings;
  const meta = settings ? liveStateMeta(settings.liveState) : null;
  const reload = () => void overview.reload();

  return (
    <>
      <PanelHeader
        pill={meta && <Pill tone={meta.tone} label={meta.label} pulse={settings?.liveState === 'live'} />}
        note={settings?.liveState === 'paused' ? `Paused: ${settings.pausedReason || 'Paused by owner'}` : null}
      />

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {overview.data ? (
          <>
            <AccountsSection appId={appId} overview={overview.data} onChanged={reload} />
            <SettingsSection appId={appId} settings={overview.data.settings} onSaved={reload} />
            <GoLiveCard
              appId={appId}
              settings={overview.data.settings}
              checklist={overview.data.checklist}
              onChanged={reload}
            />
          </>
        ) : overview.error ? (
          <LoadError message="Could not load your payment setup." onRetry={reload} />
        ) : (
          <Section title="Setup" icon="i-ph:bank">
            <SkeletonCard lines={3} />
            <SkeletonCard />
          </Section>
        )}

        {/* Waits for the overview only to pick its default filter (live once live);
            a failed overview still shows the list. Remounts when the live state flips. */}
        {overview.data || overview.error ? (
          <PaymentsList
            key={settings?.liveState ?? 'unknown'}
            appId={appId}
            defaultMode={settings && settings.liveState !== 'test' ? 'live' : 'test'}
          />
        ) : (
          <Section title="Payments" icon="i-ph:receipt">
            <SkeletonCard lines={3} />
          </Section>
        )}

        <OffersSection appId={appId} currency={settings?.currency ?? null} />
      </div>
    </>
  );
}
